/**
 * NavDrawer — mobile (< md) slide-in navigation. Driven by the same
 * `src/navigation.ts` config as LeftNavRail. Focus-trapped, Escape closes.
 */
import { useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useIntl } from 'react-intl';
import { X, LogOut, UserCircle, Search, Bell } from 'lucide-react';
import { useAuthStore } from '../stores/authStore';
import { useNotificationStore } from '../stores/notificationStore';
import { ROLE_LABELS, ROLE_COLORS, roleMessageId } from '../utils/roles';
import { visibleNavItems, isNavItemActive, type NavItem } from '../navigation';
import { useFeatures } from '../hooks/useFeatures';
import { useLogout } from '../shell/useLogout';
import { useFocusTrap } from './ui/useFocusTrap';
import type { ConnectionState } from '../services/websocket';

interface NavDrawerProps {
  open: boolean;
  onClose: () => void;
  connectionState?: ConnectionState;
  onOpenPalette?: () => void;
}

export default function NavDrawer({ open, onClose, connectionState = 'disconnected', onOpenPalette }: NavDrawerProps) {
  const intl = useIntl();
  const navigate = useNavigate();
  const location = useLocation();
  const { role, driverName } = useAuthStore();
  const { features } = useFeatures();
  const logout = useLogout();
  const unreadCount = useNotificationStore(s => s.unreadCount);
  const togglePanel = useNotificationStore(s => s.togglePanel);
  const panelRef = useRef<HTMLDivElement>(null);
  const handleEscape = useCallback(() => onClose(), [onClose]);
  useFocusTrap(panelRef, open, { onEscape: handleEscape });

  const t = (id: string, defaultMessage: string) => intl.formatMessage({ id, defaultMessage });

  function handleNav(path: string) { onClose(); navigate(path); }
  function handleLogout() { onClose(); logout(); }

  const items = visibleNavItems(role, features);
  const groups: { key: string; label: string; items: NavItem[]; accent: 'indigo' | 'amber' | 'muted' }[] = [
    { key: 'platform', label: t('shell.group.platform', 'Platform'), items: items.filter(i => i.group === 'platform'), accent: 'amber' as const },
    { key: 'main', label: t('shell.group.workspace', 'Workspace'), items: items.filter(i => i.group === 'main'), accent: 'indigo' as const },
    { key: 'legacy', label: t('shell.group.legacy', 'Legacy'), items: items.filter(i => i.group === 'legacy'), accent: 'muted' as const },
  ].filter(g => g.items.length > 0);

  const impersonatedAccountName = localStorage.getItem('fleet_impersonated_account_name');
  const initials = driverName ? driverName.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase() : '?';
  const liveLabel = connectionState === 'connected' ? t('shell.live.connected', 'Live') : connectionState === 'connecting' ? t('shell.live.connecting', 'Reconnecting…') : t('shell.live.offline', 'Offline');
  const liveDot = connectionState === 'connected' ? 'bg-hos-green' : connectionState === 'connecting' ? 'bg-hos-amber' : 'bg-hos-red';

  return (
    <>
      <div
        className={`fixed inset-0 bg-black/40 z-40 transition-opacity duration-200 ${open ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}
        onClick={onClose}
        aria-hidden="true"
      />
      <div
        ref={panelRef}
        className={`fixed top-0 left-0 h-full w-72 max-w-[85vw] bg-surface shadow-2xl z-50 flex flex-col transition-transform duration-200 ease-in-out ${open ? 'translate-x-0' : '-translate-x-full'}`}
        role="dialog"
        aria-modal="true"
        aria-label={t('shell.navMenu', 'Navigation menu')}
        aria-hidden={!open}
        tabIndex={-1}
      >
        <div className="bg-brand-900 text-white px-4 py-4 flex items-center justify-between shrink-0">
          <div className="flex items-center gap-3">
            <img src="/logos/globe-white.png" alt="DeliveryOps" className="h-7 w-7 flex-shrink-0" style={{ objectFit: 'contain' }} />
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-white font-semibold text-sm">DeliveryOps</h2>
                {(import.meta.env.VITE_ENVIRONMENT === 'qa') && <span className="bg-amber-500 text-amber-950 text-xs font-bold px-1.5 py-0.5 rounded">QA</span>}
              </div>
              {impersonatedAccountName && <p className="text-indigo-300 text-xs">{impersonatedAccountName}</p>}
            </div>
          </div>
          <button onClick={onClose} className="text-indigo-300 hover:text-white p-1 rounded transition-colors focus-ring" aria-label={t('common.close', 'Close')}>
            <X className="w-5 h-5" />
          </button>
        </div>

        <button onClick={() => handleNav('/profile')} className="w-full px-4 py-3 border-b border-line shrink-0 hover:bg-surface-sunken transition-colors text-left focus-ring">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-brand-700 flex items-center justify-center text-white font-bold text-sm shrink-0">{initials}</div>
            <div className="min-w-0 flex-1">
              <p className="font-semibold text-sm text-fg truncate">{driverName ?? 'User'}</p>
              {role && (
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${ROLE_COLORS[role] ?? 'bg-gray-100 text-gray-600'}`}>
                  {intl.formatMessage({ id: roleMessageId(role), defaultMessage: ROLE_LABELS[role] ?? role })}
                </span>
              )}
            </div>
            <UserCircle className="w-5 h-5 text-fg-subtle shrink-0" />
          </div>
        </button>

        <div className="px-2 pt-2 flex gap-1 shrink-0">
          <button type="button" onClick={() => { onClose(); onOpenPalette?.(); }} className="flex-1 flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-fg-muted hover:bg-surface-sunken focus-ring">
            <Search className="w-4 h-4" /> {t('shell.search', 'Search')}
          </button>
          <button type="button" onClick={() => { onClose(); togglePanel(); }} className="relative flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-fg-muted hover:bg-surface-sunken focus-ring" aria-label={t('shell.notifications', 'Notifications')}>
            <Bell className="w-4 h-4" />
            {unreadCount > 0 && <span className="absolute -top-0.5 right-0 h-4 min-w-4 px-1 rounded-full bg-red-500 text-white text-xs font-bold flex items-center justify-center">{unreadCount > 99 ? '99+' : unreadCount}</span>}
          </button>
        </div>

        <nav className="flex-1 px-2 py-3 overflow-y-auto">
          {groups.map(g => (
            <div key={g.key} className="mb-3">
              <p className="px-2 mb-1 text-xs font-semibold text-fg-subtle uppercase tracking-wider">{g.label}</p>
              <ul className="space-y-0.5">
                {g.items.map(item => {
                  const isActive = isNavItemActive(item, location.pathname);
                  const Icon = item.icon;
                  const activeCls = g.accent === 'amber' ? 'bg-amber-50 text-amber-700' : 'bg-brand-50 text-brand-700';
                  const iconCls = isActive ? (g.accent === 'amber' ? 'text-amber-600' : 'text-brand-600') : 'text-fg-subtle';
                  return (
                    <li key={item.id}>
                      <button
                        onClick={() => handleNav(item.path)}
                        aria-current={isActive ? 'page' : undefined}
                        className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors focus-ring ${isActive ? activeCls : 'text-fg hover:bg-surface-sunken'}`}
                      >
                        <span className={iconCls}><Icon className="w-5 h-5" /></span>
                        {t(item.labelId, item.label)}
                        {isActive && <span className="ml-auto w-1.5 h-1.5 rounded-full bg-current" />}
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </nav>

        <div className="px-4 py-2 border-t border-line text-xs text-fg-muted flex items-center gap-2 shrink-0" role="status">
          <span className={`w-2 h-2 rounded-full ${liveDot}`} aria-hidden /> {liveLabel}
        </div>

        <div className="px-2 pb-2 border-t border-line pt-2 shrink-0">
          <button onClick={handleLogout} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-red-600 hover:bg-red-50 transition-colors focus-ring">
            <LogOut className="w-5 h-5" />
            {t('auth.signOut', 'Sign out')}
          </button>
        </div>

        <div className="px-4 py-3 border-t border-line shrink-0">
          <p className="text-xs text-fg-subtle text-center">Powered by Savintosh Solutions</p>
          <p className="text-xs text-fg-subtle text-center">v2.1.0</p>
        </div>
      </div>
    </>
  );
}
